import { type ReactNode, useCallback, useEffect } from 'react';

import type { AxiosRequestConfig } from 'axios';
import { useAtom } from 'jotai';
import useSWRInfinite, { type SWRInfiniteKeyLoader } from 'swr/infinite';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@quick/components/Sheet';
import type { Paginate } from '@quick/go-api-types/common';
import { type NotificationGet } from '@quick/go-api-types/notifications';

import { useFetch } from '@/layers/core/deal/services/useFetch';
import { useUser } from '@/layers/core/deal/services/useUser';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

import { notificationsOpenAtom } from './atoms';
import { LoadMore } from './LoadMore';
import { Notification } from './Notification';
import { useNotificationsContext } from './NotificationsContext';

export function NotificationInteractable({
  children,
  ...props
}: {
  className: string;
  title: string;
  children: ReactNode;
}) {
  const { token } = useUser();
  const getKey = useCallback<SWRInfiniteKeyLoader<Paginate<NotificationGet>>>(
    (index, previous) => {
      if (!token) return null;
      if (previous && !previous.next_page_url) return null;
      return {
        url: '/notifications',
        headers: {
          Authorization: token,
        },
        params: { page: index + 1 },
      };
    },
    [token]
  );
  const notificationPages = useSWRInfinite<Paginate<NotificationGet>>(
    getKey,
    fetcher,
    { revalidateAll: true, persistSize: true }
  );
  const { data, mutate } = notificationPages;

  const { mutateRef, t } = useNotificationsContext();
  mutateRef.current = mutate;

  const { mutate: mutateNotificationsInfo } = useFetch({
    url: '/notifications/info',
    auth: true,
    immutable: true,
  });

  const update = useCallback(() => {
    mutate();
    mutateNotificationsInfo();
  }, [mutate, mutateNotificationsInfo]);

  useEffect(() => {
    const t = setInterval(update, 5 * 60 * 1000); // 5 mins
    return () => clearInterval(t);
  }, [update]);

  const length = data?.at?.(0)?.total;

  const [open, setOpen] = useAtom(notificationsOpenAtom);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger {...props}>{children}</SheetTrigger>
      <SheetContent
        size="content"
        className="flex max-w-[calc(100vw-5rem)] flex-col"
      >
        <SheetHeader>
          <SheetTitle>{t('OpPl:menu.notifications')}</SheetTitle>
          {/* <SheetDescription>Hola Mundo</SheetDescription> */}
        </SheetHeader>
        {length ? (
          <>
            <div className="grid grid-cols-2">
              <button
                onClick={async () => {
                  try {
                    await axios_demand.patch(
                      '/notifications/mark_all_as_read',
                      {},
                      { headers: { Authorization: token } }
                    );
                    update();
                  } catch (_e) {
                    //
                  }
                }}
              >
                {t('OpPl:notifications.readAll')}
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios_demand.delete('/notifications/delete_all', {
                      data: {},
                      headers: { Authorization: token },
                    });
                    update();
                  } catch (_e) {
                    //
                  }
                }}
              >
                {t('OpPl:notifications.deleteAll')}
              </button>
            </div>
            <div className="flex flex-col gap-2 overflow-y-auto">
              {data.map(({ data }) =>
                data.map((props) => <Notification key={props.id} {...props} />)
              )}
              <LoadMore {...notificationPages} />
            </div>
          </>
        ) : (
          t('OpPl:notifications.no')
        )}
      </SheetContent>
    </Sheet>
  );
}

const fetcher = (config: AxiosRequestConfig) =>
  axios_demand({
    ...config,
    method: 'GET',
    data: {},
  }).then((res) => res.data);
